/**
 * Login page — ported from dashboard/app/login/page.tsx.
 * Uses TanStack Router APIs instead of next/navigation.
 */
import { useState, useEffect } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { login, getCurrentUser } from '../lib/auth-client'
import { BACKEND_URL } from '../lib/api'
import { sanitizeRedirectPath } from '../lib/redirect'
import { track } from '../lib/track'
import { identifyUser } from '../lib/mixpanel'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { registered?: boolean; reset?: boolean; error?: string; redirect?: string } => ({
    registered: search.registered === 'true' ? true : undefined,
    reset: search.reset === 'true' ? true : undefined,
    error: (search.error as string) || undefined,
    redirect: (search.redirect as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: 'Login | Unbreakable' }],
  }),
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { registered, reset: passwordReset, error: oauthError, redirect: redirectTo } = Route.useSearch()
  const safeRedirectPath = sanitizeRedirectPath(redirectTo).split(/[?#]/)[0] || '/'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    async function checkAuth() {
      try {
        const user = await getCurrentUser()
        if (user) {
          // School staff go to school dashboard, not family dashboard
          if (user.tenantType === 'school' && user.tenantId) {
            navigate({ to: '/school/$schoolId', params: { schoolId: user.tenantId } })
          } else {
            navigate({ to: safeRedirectPath })
          }
          return
        }
      } catch {
        // Not authenticated
      } finally {
        setCheckingAuth(false)
      }
    }
    checkAuth()
  }, [navigate, safeRedirectPath])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  useEffect(() => {
    if (oauthError) {
      const errorMessages: Record<string, string> = {
        no_account: 'No account found with this Google email. Please sign up first.',
        oauth_failed: 'Google sign-in failed. Please try again.',
        auth_failed: 'Authentication failed. Please try again.',
        invalid_state: 'Authentication state expired. Please try again.',
        no_code: 'Authentication was cancelled. Please try again.',
        missing_code: 'Authentication was cancelled. Please try again.',
        exchange_failed: 'Authentication failed. Please try again.',
        school_password_required: 'School staff accounts must sign in with email and password.',
      }
      setError(errorMessages[oauthError] || 'An error occurred during sign-in.')
    }
  }, [oauthError])

  const handleGoogleClick = () => {
    window.location.href = `${BACKEND_URL}/api/v1/auth/oauth/google?intent=login&redirect=/`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login(email, password)
      identifyUser(result.user.id, {
        $email: result.user.email,
        ...(result.user.name ? { $name: result.user.name } : {}),
      })
      track({ name: 'logged_in' })
      if (result.accountType === 'school' && result.school) {
        navigate({ to: '/school/$schoolId', params: { schoolId: result.school.id } })
      } else {
        navigate({ to: safeRedirectPath })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred')
      setLoading(false)
    }
  }

  if (checkingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-bg p-4">
        <div className="w-full max-w-4xl rounded-2xl bg-white shadow-lg overflow-hidden">
          <div className="flex justify-center p-12" role="status">
            <div className="h-8 w-8 motion-safe:animate-spin rounded-full border-b-2 border-brand" />
            <span className="sr-only">Loading</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page-bg p-4">
      <div className="flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:flex-row">
        {/* Left Panel - Form */}
        <div className="flex-1 p-8 md:p-12">
          <div className="mb-8">
            <h1 className="mb-1 text-2xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-gray-500">Sign in to your account to continue</p>
          </div>

          {registered && (
            <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-green-800">Registration successful! Please sign in.</p>
            </div>
          )}
          {passwordReset && (
            <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-green-800">
                Password reset successful! Please sign in with your new password.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="m@example.com"
                className="h-11 px-4"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 px-4"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-hover active:bg-brand-active focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Divider + social login */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-3 text-gray-400">Or continue with</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              disabled
              className="flex cursor-not-allowed items-center justify-center rounded-lg border border-gray-200 px-4 py-3 opacity-50"
              aria-label="Sign in with Apple (coming soon)"
            >
              <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
            </button>

            <button
              type="button"
              onClick={handleGoogleClick}
              disabled={loading}
              className="flex items-center justify-center rounded-lg border border-gray-200 px-4 py-3 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Sign in with Google"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </button>

            <button
              type="button"
              disabled
              className="flex cursor-not-allowed items-center justify-center rounded-lg border border-gray-200 px-4 py-3 opacity-50"
              aria-label="Sign in with Facebook (coming soon)"
            >
              <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
          </div>

          {/* Links */}
          <div className="mt-6 flex justify-center gap-6 text-sm">
            <Link to="/forgot-password" className="text-brand hover:underline">
              Forgot Password
            </Link>
            <Link to="/register" className="text-brand hover:underline">
              Sign Up
            </Link>
          </div>
        </div>

        {/* Right Panel - Branding */}
        <div className="hidden items-center justify-center bg-brand-light md:flex md:w-[340px]">
          <span
            className="select-none text-[180px] leading-none text-brand"
            style={{
              fontFamily: '"Franklin Gothic Medium", "Arial Narrow Bold", sans-serif',
              fontWeight: 900,
            }}
          >
            U
          </span>
        </div>
      </div>
    </div>
  )
}
